import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Badge } from '../../../components/ui/badge';
import { Camera, Video } from 'lucide-react';
import { useRace } from '../../../hooks/useRaces';
import { usePublicResults, usePublicTeamResults, usePublicResultsSummary } from '../../../hooks/useResults';
import { usePublicSeriesById } from '../../../hooks/useRaceSeries';
import en from '../../../locales/en.json';
import mk from '../../../locales/mk.json';
import al from '../../../locales/al.json';
import RaceResultsView from './raceresultspage.view';

const localeMap = { en, mk, al };

function getInitialLang(): 'en' | 'mk' | 'al' {
  const stored = localStorage.getItem('lang');
  if (stored === 'en' || stored === 'mk' || stored === 'al') return stored;
  return 'mk';
}

const STATUS_MAP = { 0: 'Finished', 1: 'DNF', 2: 'DNS', 3: 'DSQ' };

function RankBadge({ rank }) {
  if (!rank) return <span className="w-8 text-center text-muted-foreground">-</span>;
  if (rank <= 3) {
    const colors = ['', 'bg-amber-400 text-white', 'bg-gray-400 text-white', 'bg-amber-700 text-white'];
    return (
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${colors[rank]}`}>
        {rank}
      </span>
    );
  }
  return <span className="w-8 text-center font-medium text-muted-foreground">{rank}</span>;
}

export default function RaceResultsPage() {
  const { id: raceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [lang, setLang] = useState(getInitialLang());
  const t = localeMap[lang];

  useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  const [expandedRow, setExpandedRow] = useState(null);

  // URL-synced filters
  const raceTypeId = searchParams.get('raceTypeId') || '';
  const gender = searchParams.get('gender') || '';
  const ageGroup = searchParams.get('ageGroup') || '';
  const search = searchParams.get('search') || '';
  const view = searchParams.get('view') || 'overall';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const updateParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.delete('page');
    setSearchParams(params);
  };

  // TanStack queries
  const { data: race } = useRace(raceId);
  const { data: summary } = usePublicResultsSummary(raceId);

  const resultsParams = useMemo(() => ({
    raceTypeId: raceTypeId || undefined,
    gender: gender || undefined,
    ageGroup: (view === 'ageGroups' && ageGroup) ? ageGroup : undefined,
    search: search || undefined,
    page,
    pageSize: 50,
  }), [raceTypeId, gender, ageGroup, search, view, page]);

  const teamParams = useMemo(() => ({
    raceTypeId: raceTypeId || undefined,
  }), [raceTypeId]);

  const { data: resultsData, isLoading: resultsLoading } = usePublicResults(
    view !== 'teams' ? raceId : null,
    resultsParams
  );
  const { data: teamsData, isLoading: teamsLoading } = usePublicTeamResults(
    view === 'teams' ? raceId : null,
    teamParams
  );

  const results = resultsData?.items || resultsData || [];
  const total = resultsData?.total || 0;
  const teams = teamsData || [];
  const loading = view === 'teams' ? teamsLoading : resultsLoading;

  const { data: seriesData } = usePublicSeriesById(race?.raceSeriesId);
  const seriesRaces = seriesData?.races || [];

  const rr: Record<string, any> = t.rr || {};

  const totalPages = Math.ceil(total / 50);
  const raceTypes = race?.raceTypes || [];

  const resultColumns = useMemo(() => {
    const cols: Array<{ key: string; label: any; className?: string; cellClassName?: string; render?: (v: any, r: any) => any }> = [
      { key: 'overallRank', label: '#', className: 'w-14', render: (v) => <RankBadge rank={v} /> },
      { key: 'bibNumber', label: 'BIB', className: 'w-16', cellClassName: 'font-mono text-muted-foreground' },
      { key: 'fullName', label: rr.name || 'Name', render: (v, r) => (
        <div><div className="font-medium">{v}</div>{r.club && <div className="text-xs text-muted-foreground">{r.club}</div>}</div>
      )},
      { key: 'gender', label: rr.gender || 'Gender', className: 'w-12', render: (v) => v === 'M' ? (rr.male || 'Male') : v === 'F' ? (rr.female || 'Female') : '' },
    ];
    if (view === 'ageGroups') {
      cols.push({ key: 'ageGroupName', label: rr.group || 'Group', render: (v) => <Badge variant="outline" className="text-xs">{v || '-'}</Badge> });
    }
    cols.push(
      { key: 'chipTime', label: rr.time || 'Time', cellClassName: 'font-mono font-medium', render: (_, r) => r.status === 0 ? (r.chipTime || r.gunTime || '-') : STATUS_MAP[r.status] },
      { key: 'pace', label: rr.pace || 'Pace', cellClassName: 'font-mono text-muted-foreground', render: (v) => v || '-' },
      { key: 'genderRank', label: rr.ranks || 'Ranks', render: (_, r) => (
        <div className="flex gap-1 flex-wrap">
          {r.genderRank && <Badge variant="secondary" className="text-xs">G:{r.genderRank}</Badge>}
          {r.ageGroupRank && <Badge variant="outline" className="text-xs">AG:{r.ageGroupRank}</Badge>}
        </div>
      )},
      { key: 'photoLink', label: '📷', className: 'w-16 text-center', cellClassName: 'text-center', render: (v) => v
        ? <a href={v} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-blue-100 text-blue-600 transition-colors" title={rr.photo || 'Photo'} onClick={e => e.stopPropagation()}><Camera className="h-4 w-4" /></a>
        : <span className="text-muted-foreground/30">-</span>
      },
      { key: 'videoLink', label: '🎬', className: 'w-16 text-center', cellClassName: 'text-center', render: (v) => v
        ? <a href={v} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-red-100 text-red-600 transition-colors" title={rr.video || 'Video'} onClick={e => e.stopPropagation()}><Video className="h-4 w-4" /></a>
        : <span className="text-muted-foreground/30">-</span>
      },
    );
    return cols;
  }, [view, rr]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: `${rr.results || 'Results'} - ${race?.name || ''}`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <RaceResultsView
      lang={lang}
      setLang={setLang}
      t={t}
      rr={rr}
      race={race}
      summary={summary}
      results={results}
      teams={teams}
      total={total}
      loading={loading}
      expandedRow={expandedRow}
      setExpandedRow={setExpandedRow}
      raceTypeId={raceTypeId}
      gender={gender}
      ageGroup={ageGroup}
      search={search}
      view={view}
      page={page}
      totalPages={totalPages}
      raceTypes={raceTypes}
      seriesRaces={seriesRaces}
      raceId={raceId}
      resultColumns={resultColumns}
      updateParam={updateParam}
      handleShare={handleShare}
      navigate={navigate}
    />
  );
}
